import moment from 'moment';
import DateUtil from '../utils/DateUtil';
import {TSSchadenArt} from './enums/TSSchadenArt';
import {TSSchadenObjektTyp} from './enums/TSSchadenObjekt';
import TSAbstractEntity from './TSAbstractEntity';
import TSSchadenObjektDaten from './TSSchadenObjektDaten';
import {TSSerializable} from './TSSerializable';

export default class TSSchadenDaten extends TSAbstractEntity implements TSSerializable<TSSchadenDaten> {

    private _schadendatum: moment.Moment | undefined;

    private _ort: string;

    private _mapLongitude: number;

    private _mapLatitude: number;

    private _schadenArt: TSSchadenArt;

    private _schadenObjekt: TSSchadenObjektTyp;

    private _schadenObjektDaten: TSSchadenObjektDaten;

    private _beschrieb: string;

    private _flurname: string;

    private _parzellennummer: string;

    private _vorgeseheneMassnahmen: string;

    private _geschaetzteKosten: number | undefined;

    private _geschaetzterMehrwert: number | undefined;

    private _geschaedigteFlaeche: number;

    private _schadenArtAndere: string;

    private _otherwieseSubventionierbar: boolean;
    private _offerte: boolean;
    private _arbeiten: boolean;

    public get schadendatum(): moment.Moment | undefined {
        return this._schadendatum;
    }

    public set schadendatum(value: moment.Moment | undefined) {
        this._schadendatum = value;
    }

    public get ort(): string {
        return this._ort;
    }

    public set ort(value: string) {
        this._ort = value;
    }

    public get mapLongitude(): number {
        return this._mapLongitude;
    }

    public set mapLongitude(value: number) {
        this._mapLongitude = value;
    }

    public get mapLatitude(): number {
        return this._mapLatitude;
    }

    public set mapLatitude(value: number) {
        this._mapLatitude = value;
    }

    public get beschrieb(): string {
        return this._beschrieb;
    }

    public set beschrieb(value: string) {
        this._beschrieb = value;
    }

    public get flurname(): string {
        return this._flurname;
    }

    public set flurname(value: string) {
        this._flurname = value;
    }

    public get parzellennummer(): string {
        return this._parzellennummer;
    }

    public set parzellennummer(value: string) {
        this._parzellennummer = value;
    }

    public get vorgeseheneMassnahmen(): string {
        return this._vorgeseheneMassnahmen;
    }

    public set vorgeseheneMassnahmen(value: string) {
        this._vorgeseheneMassnahmen = value;
    }

    public get geschaetzteKosten(): number | undefined {
        return this._geschaetzteKosten;
    }

    public set geschaetzteKosten(value: number | undefined) {
        this._geschaetzteKosten = value;
    }

    public get geschaetzterMehrwert(): number | undefined {
        return this._geschaetzterMehrwert;
    }

    public set geschaetzterMehrwert(value: number | undefined) {
        this._geschaetzterMehrwert = value;
    }

    public get geschaedigteFlaeche(): number {
        return this._geschaedigteFlaeche;
    }

    public set geschaedigteFlaeche(value: number) {
        this._geschaedigteFlaeche = value;
    }

    public get schadenArt(): TSSchadenArt {
        return this._schadenArt;
    }

    public set schadenArt(value: TSSchadenArt) {
        this._schadenArt = value;
    }

    public get schadenObjekt(): TSSchadenObjektTyp {
        return this._schadenObjekt;
    }

    public set schadenObjekt(value: TSSchadenObjektTyp) {
        this._schadenObjekt = value;
    }

    public get schadenObjektDaten(): TSSchadenObjektDaten {
        return this._schadenObjektDaten;
    }

    public set schadenObjektDaten(value: TSSchadenObjektDaten) {
        this._schadenObjektDaten = value;
    }

    public get coordinatesAsString(): string {
        if (this.mapLatitude && this.mapLongitude) {
            let latstring = (Number(this.mapLatitude)).toFixed(9);
            let longstring = (Number(this.mapLongitude)).toFixed(9);
            return latstring + ' / ' + longstring;
        } else {
            return '- / -';
        }
    }

    public get coordinatesAsLV95String(): string {
        if (this.mapLatitude && this.mapLongitude) {
            // umrechnen in lv95 Schweizer-Koordinaten
            // @ts-ignore
            let swissCoordLV95 = ol.proj.transform([this.mapLongitude, this.mapLatitude], 'EPSG:4326', 'EPSG:2056');
            return this.getAprostropheString(swissCoordLV95[0]) + ' / ' + this.getAprostropheString(swissCoordLV95[1]);
        } else {
            return '- / -';
        }
    }

    private getAprostropheString(swissCoordLV95: number) {
        let s = swissCoordLV95.toFixed(0);
        return s.substr(0, 1) + '\'' + s.substr(1, 3) + '\'' + s.substr(4, 3);
    }

    public get schadenArtAndere(): string {
        return this._schadenArtAndere;
    }

    public set schadenArtAndere(value: string) {
        this._schadenArtAndere = value;
    }

    public get otherwieseSubventionierbar(): boolean {
        return this._otherwieseSubventionierbar;
    }

    public set otherwieseSubventionierbar(value: boolean) {
        this._otherwieseSubventionierbar = value;
    }

    public get offerte(): boolean {
        return this._offerte;
    }

    public set offerte(value: boolean) {
        this._offerte = value;
    }

    public get arbeiten(): boolean {
        return this._arbeiten;
    }

    public set arbeiten(value: boolean) {
        this._arbeiten = value;
    }

    deserialize(restObject: any): TSSchadenDaten {
        super.deserialize(restObject);
        this.schadendatum = DateUtil.localDateToMoment(restObject.schadendatum);
        this.ort = restObject.ort;
        this.mapLongitude = restObject.mapLongitude;
        this.mapLatitude = restObject.mapLatitude;
        this.beschrieb = restObject.beschrieb;
        this.flurname = restObject.flurname;
        this.parzellennummer = restObject.parzellennummer;
        this.vorgeseheneMassnahmen = restObject.vorgeseheneMassnahmen;
        this.geschaetzteKosten = restObject.geschaetzteKosten;
        this.geschaetzterMehrwert = restObject.geschaetzterMehrwert;
        this.geschaedigteFlaeche = restObject.geschaedigteFlaeche;
        this.schadenArt = restObject.schadenArt;
        this.schadenObjekt = restObject.schadenObjekt;
        this.schadenObjektDaten = this.deserializeOrCreate(restObject.schadenObjektDaten, new TSSchadenObjektDaten());
        this.schadenArtAndere = restObject.schadenArtAndere;
        this.otherwieseSubventionierbar = restObject.otherwieseSubventionierbar;
        this.offerte = restObject.offerte;
        this.arbeiten = restObject.arbeiten;

        return this;
    }

    serialize(restObject: any): any {
        super.serialize(restObject);
        if (this.schadendatum) {
            restObject.schadendatum = DateUtil.momentToLocalDate(this.schadendatum);
        }
        restObject.ort = this.ort;
        restObject.mapLongitude = this.mapLongitude;
        restObject.mapLatitude = this.mapLatitude;
        restObject.beschrieb = this.beschrieb;
        restObject.flurname = this.flurname;
        restObject.parzellennummer = this.parzellennummer;
        restObject.vorgeseheneMassnahmen = this.vorgeseheneMassnahmen;
        restObject.geschaetzteKosten = this.geschaetzteKosten;
        restObject.geschaetzterMehrwert = this.geschaetzterMehrwert;
        restObject.geschaedigteFlaeche = this.geschaedigteFlaeche;
        restObject.schadenArt = this.schadenArt;
        restObject.schadenObjekt = this.schadenObjekt;
        restObject.schadenObjektDaten = this.serializeOrInit(this.schadenObjektDaten, restObject.schadenObjektDaten);
        restObject.schadenArtAndere = this.schadenArtAndere;
        restObject.otherwieseSubventionierbar = this.otherwieseSubventionierbar;
        restObject.offerte = this.offerte;
        restObject.arbeiten = this.arbeiten;

        return restObject;
    }
}



// WEBPACK FOOTER //
// ./src/app/models/TSSchadenDaten.ts